import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const IntroAnimation = ({ onComplete }) => {
  const [showAnimation, setShowAnimation] = useState(true);
  const [phase, setPhase] = useState(1); // 1: lantern appears, 2: walks path, 3: text reveals

  useEffect(() => {
    // Always show intro animation on app load
    // Phase transitions
    const phase2Timer = setTimeout(() => setPhase(2), 500);
    const phase3Timer = setTimeout(() => setPhase(3), 2500);
    
    const completeTimer = setTimeout(() => {
      setShowAnimation(false);
      onComplete?.();
    }, 4000);

    return () => {
      clearTimeout(phase2Timer);
      clearTimeout(phase3Timer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  if (!showAnimation) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] bg-[#0A0E1A] flex items-center justify-center overflow-hidden"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Stars background */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
              }}
              animate={{
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Ground gradient */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-1/3"
          style={{
            background: 'linear-gradient(to top, rgba(30, 20, 10, 0.8) 0%, transparent 100%)',
          }}
        />

        <div className="relative w-full max-w-lg h-64 px-8">
          {/* Dashed Dirt Path - more detailed */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 400 200"
            fill="none"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Path shadow */}
            <motion.path
              d="M 20 160 Q 100 130 180 140 Q 260 150 320 120 Q 380 90 380 70"
              stroke="rgba(80, 60, 40, 0.4)"
              strokeWidth="20"
              strokeLinecap="round"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: phase >= 2 ? 1 : 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
            />
            {/* Main path */}
            <motion.path
              d="M 20 160 Q 100 130 180 140 Q 260 150 320 120 Q 380 90 380 70"
              stroke="rgba(139, 115, 85, 0.7)"
              strokeWidth="4"
              strokeDasharray="12 8"
              strokeLinecap="round"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: phase >= 2 ? 1 : 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
            />
            {/* Path highlights */}
            <motion.path
              d="M 20 158 Q 100 128 180 138 Q 260 148 320 118 Q 380 88 380 68"
              stroke="rgba(180, 150, 100, 0.3)"
              strokeWidth="2"
              strokeDasharray="4 16"
              strokeLinecap="round"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: phase >= 2 ? 1 : 0 }}
              transition={{ duration: 2, ease: "easeOut", delay: 0.3 }}
            />
          </svg>

          {/* Gold Lantern - animated along path */}
          <motion.div
            className="absolute"
            initial={{ x: 20, y: 120, opacity: 0, scale: 0.8 }}
            animate={phase >= 2 ? {
              x: [20, 100, 180, 260, 340],
              y: [120, 95, 105, 85, 45],
              opacity: 1,
              scale: 1,
              rotate: [0, -5, 5, -3, 0],
            } : { opacity: 0 }}
            transition={{
              duration: 2.5,
              ease: "easeInOut",
              times: [0, 0.25, 0.5, 0.75, 1],
            }}
          >
            {/* Lantern glow aura */}
            <motion.div
              className="absolute -inset-8 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(255, 215, 0, 0.4) 0%, rgba(255, 215, 0, 0.1) 40%, transparent 70%)',
              }}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            
            {/* Ground light reflection */}
            <motion.div
              className="absolute top-12 left-1/2 -translate-x-1/2 w-16 h-8 rounded-full"
              style={{
                background: 'radial-gradient(ellipse, rgba(255, 215, 0, 0.3) 0%, transparent 70%)',
              }}
              animate={{
                scaleX: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{ duration: 1, repeat: Infinity }}
            />

            {/* Lantern SVG - detailed */}
            <svg width="40" height="56" viewBox="0 0 40 56" className="relative z-10">
              {/* Hook */}
              <path d="M 20 0 Q 20 4 20 8" stroke="#B8860B" strokeWidth="2" fill="none" />
              <circle cx="20" cy="10" r="3" fill="#DAA520" />
              
              {/* Top cap */}
              <path d="M 12 12 L 28 12 L 26 16 L 14 16 Z" fill="#DAA520" />
              
              {/* Body frame */}
              <rect x="10" y="16" width="20" height="32" fill="none" stroke="#FFD700" strokeWidth="2" rx="3" />
              
              {/* Glass with animated glow */}
              <motion.rect
                x="12"
                y="18"
                width="16"
                height="28"
                fill="rgba(255, 215, 0, 0.2)"
                rx="2"
                animate={{
                  fill: [
                    "rgba(255, 215, 0, 0.2)",
                    "rgba(255, 215, 0, 0.4)",
                    "rgba(255, 215, 0, 0.2)",
                  ],
                }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
              
              {/* Decorative bars */}
              <line x1="20" y1="18" x2="20" y2="46" stroke="#DAA520" strokeWidth="1" opacity="0.5" />
              
              {/* Flame */}
              <motion.ellipse
                cx="20"
                cy="32"
                fill="#FFD700"
                animate={{
                  ry: [8, 10, 6, 9, 8],
                  rx: [4, 5, 3, 4, 4],
                }}
                transition={{ duration: 0.5, repeat: Infinity }}
              />
              <motion.ellipse
                cx="20"
                cy="30"
                fill="#FFE55C"
                animate={{
                  ry: [4, 6, 3, 5, 4],
                  rx: [2, 3, 1.5, 2, 2],
                }}
                transition={{ duration: 0.4, repeat: Infinity }}
              />
              <motion.ellipse
                cx="20"
                cy="28"
                rx="1"
                fill="#FFFFFF"
                animate={{
                  ry: [1.5, 2.5, 1, 2, 1.5],
                }}
                transition={{ duration: 0.3, repeat: Infinity }}
              />
              
              {/* Bottom cap */}
              <path d="M 14 48 L 26 48 L 28 52 L 12 52 Z" fill="#DAA520" />
              <rect x="16" y="52" width="8" height="3" fill="#B8860B" rx="1" />
            </svg>
          </motion.div>

          {/* Text reveals */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center w-full">
            <motion.h1
              className="font-heading text-5xl md:text-6xl font-bold mb-2"
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFF8DC 25%, #FFD700 50%, #DAA520 75%, #FFD700 100%)',
                backgroundSize: '200% 200%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={phase >= 3 ? { 
                opacity: 1, 
                y: 0,
                backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
              } : {}}
              transition={{ 
                duration: 0.8,
                backgroundPosition: { duration: 3, repeat: Infinity }
              }}
            >
              Lumiveta
            </motion.h1>
            
            <motion.p
              className="text-lg text-[#C7A882] tracking-wider"
              initial={{ opacity: 0, y: 20 }}
              animate={phase >= 3 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Light for your path
            </motion.p>

            <motion.div
              className="mt-4 flex justify-center gap-1"
              initial={{ opacity: 0 }}
              animate={phase >= 3 ? { opacity: 1 } : {}}
              transition={{ delay: 0.6 }}
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 rounded-full bg-primary"
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </motion.div>
          </div>
        </div>

        {/* Skip button */}
        <motion.button
          className="absolute bottom-8 right-8 text-sm text-muted-foreground hover:text-foreground transition-colors"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 1 }}
          onClick={() => {
            setShowAnimation(false);
            onComplete?.();
          }}
        >
          Skip →
        </motion.button>
      </motion.div>
    </AnimatePresence>
  );
};

export default IntroAnimation;
